import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCarSide, faXmark, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {string, func, arrayOf, shape, number, oneOfType} from "prop-types";
import { ETA } from './gmproTokens';

// The /grouped_trips panel is rendered at transform: scale(0.9); the portaled
// dropdown lives outside that wrapper, so it is scaled to match.
const PANEL_SCALE = 0.9;

// The decline_reason enum keys split by rejection type. Rejecting the driver
// rejects the recommended driver-and-vehicle pairing, so vehicle-fit reasons sit
// here too. Rejecting the grouping is about the route, trips and data.
// (Backend accepts any key for either action.)
const DRIVER_REASONS = [
  { value: 'unavailable_driver', label: 'Unavailable driver' },
  { value: 'driver_out_of_region', label: 'Driver out of region' },
  { value: 'driver_already_assigned', label: 'Driver already assigned' },
  { value: 'driver_unsuitable', label: 'Driver unsuitable' },
  { value: 'incorrect_vehicle_type', label: 'Incorrect vehicle type' },
  { value: 'underfilled_vehicle', label: 'Underfilled vehicle' },
  { value: 'other', label: 'Other' },
];
const GROUPING_REASONS = [
  { value: 'excessive_trip_duration', label: 'Excessive trip duration' },
  { value: 'excessive_detour', label: 'Excessive detour' },
  { value: 'not_worth_dedicated_vehicle', label: 'Not worth a dedicated vehicle' },
  { value: 'out_of_bounds', label: 'Out of bounds' },
  { value: 'unsafe_route', label: 'Unsafe route' },
  { value: 'passenger_request', label: 'Passenger request' },
  { value: 'booking_changed', label: 'Booking changed' },
  { value: 'other', label: 'Other' },
];
const reasonsFor = (type) => (type === 'driver' ? DRIVER_REASONS : GROUPING_REASONS);

const NOTE_MAX_LENGTH = 80;

// Two-dot step indicator — dots fill cumulatively up to the current step.
const StepDots = ({ step }) => (
  <div className="flex items-center" style={{ gap: 6 }}>
    {[1, 2].map((dot) => (
      <span
        key={dot}
        style={{
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: dot <= step ? ETA.purple : ETA.disabled,
        }}
      />
    ))}
  </div>
);

// A footer pill button (Back is grey, Submit is purple, both fall back to the
// disabled grey).
const pill = (background) => ({
  background,
  paddingLeft: 19,
  paddingRight: 19,
  paddingTop: 7,
  paddingBottom: 7,
  borderRadius: 5,
  fontSize: 16,
  color: '#fff',
});

// Custom reason dropdown matching the Figma "Reasons Dropdown" — a white trigger
// with a chevron and a white, shadowed options panel (the native <select> would
// render the OS popup, which can't match the design). Closes on select or an
// outside click.
const ReasonDropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [hovered, setHovered] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Anchor the portaled panel directly under the trigger (on-screen coords).
  const openMenu = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ left: rect.left, top: rect.bottom + 4, width: rect.width });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    // While the menu is open, keep scroll on the options panel and stop the
    // cards behind it from scrolling. The panel is position:fixed, so a
    // background scroll would otherwise slide the trigger out from under it.
    const onWheel = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      event.preventDefault();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onWheel, { passive: false });
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onWheel);
    };
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="relative" style={{ flex: 1, minWidth: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="gmpro-reject-reason-select"
        onClick={() => (open ? setOpen(false) : openMenu())}
        className="flex items-center justify-between focus:outline-none"
        style={{
          width: '100%',
          height: 32,
          padding: '0 14px',
          border: '1px solid rgba(110,108,113,0.3)',
          borderRadius: 5,
          fontSize: 12,
          color: selected ? ETA.darkGrey : ETA.placeholder,
          background: '#fff',
        }}>
        <span className="truncate">{selected ? selected.label : 'Select a reason...'}</span>
        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 10, color: ETA.darkGrey, marginLeft: 8 }} />
      </button>
      {open && pos &&
        ReactDOM.createPortal(
          <div
            ref={panelRef}
            data-testid="gmpro-reject-reason-options"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              width: pos.width / PANEL_SCALE,
              transform: `scale(${PANEL_SCALE})`,
              transformOrigin: 'top left',
              background: '#fff',
              borderRadius: 5,
              boxShadow: '0px 0px 10px 2px rgba(0,0,0,0.15)',
              zIndex: 9999,
              padding: 4,
              maxHeight: 300,
              overflowY: 'auto',
            }}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                data-testid={`gmpro-reject-reason-${option.value}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onMouseEnter={() => setHovered(option.value)}
                onMouseLeave={() => setHovered(null)}
                className="block w-full text-left focus:outline-none"
                style={{
                  padding: '6px 15px',
                  fontSize: 12,
                  color: ETA.darkGrey,
                  borderRadius: 5,
                  background: hovered === option.value ? 'rgba(116,93,150,0.1)' : 'transparent',
                }}>
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

// Two-step "Reject" slide sheet, matching the Figma Reject_Slidesheet. It slides
// in from the right over the card (the card sizes to the sheet's content).
//   Step 1 — pick Reject Driver (re-assign in Unassigned) or Reject Grouping
//            (ungroup back to Bookings). Picking one advances to step 2.
//   Step 2 — pick a reason (driver- or grouping-specific); "Other" reveals a
//            required free-text note (max 80 chars). Back returns to step 1.
// An X in the top-right closes the sheet at any step.
const GmproRejectSheet = ({ grouping, onClose, onRejectDriver, onRejectGrouping }) => {
  const [step, setStep] = useState(1);
  const [rejectType, setRejectType] = useState(null); // 'driver' | 'grouping'
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [shown, setShown] = useState(false);

  // Trigger the slide-in transition on mount.
  useEffect(() => {
    setShown(true);
  }, []);

  const noteRequired = reason === 'other';
  const submitDisabled = !reason || (noteRequired && !note.trim());

  // Slide the sheet back out to the right, then unmount.
  const handleClose = () => {
    setShown(false);
    setTimeout(onClose, 300);
  };

  // Picking a rejection type clears any prior reason and advances to step 2.
  const chooseType = (type) => {
    setRejectType(type);
    setReason('');
    setNote('');
    setStep(2);
  };

  const handleSubmit = () => {
    if (submitDisabled) return;
    const handler = rejectType === 'driver' ? onRejectDriver : onRejectGrouping;
    handler(grouping, reason, noteRequired ? note.trim() : '');
    onClose();
  };

  const optionStyle = (type) => {
    const selected = rejectType === type;
    return {
      flex: 1,
      minWidth: 0,
      height: 125,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'left',
      padding: '8px 10px 8px 15px',
      borderRadius: 10,
      border: `1.5px solid ${selected ? ETA.purple : ETA.darkGrey}`,
      background: selected ? ETA.purple : '#fff',
    };
  };
  const optionTitleColor = (type) => (rejectType === type ? '#fff' : ETA.purple);
  const optionSubColor = (type) => (rejectType === type ? '#fff' : ETA.darkGrey);

  return (
    <div
      data-testid="gmpro-reject-sheet"
      className="relative bg-white transition-transform duration-300 ease-out"
      style={{
        padding: 18,
        transform: shown ? 'translateX(0)' : 'translateX(100%)',
      }}>
      {/* Close (X) — top right, all steps, no surrounding circle */}
      <button
        data-testid="gmpro-reject-close"
        onClick={handleClose}
        className="absolute focus:outline-none"
        style={{ top: 14, right: 16, color: ETA.grey, fontSize: 18 }}>
        <FontAwesomeIcon icon={faXmark} />
      </button>

      {step === 1 && (
        <>
          <h2 style={{ fontWeight: 500, fontSize: 20, color: ETA.purple, paddingRight: 30 }}>
            Why are you rejecting the trip?
          </h2>
          <div className="flex" style={{ gap: 12, marginTop: 12 }}>
            <button
              data-testid="gmpro-reject-driver-option"
              onClick={() => chooseType('driver')}
              className="focus:outline-none"
              style={optionStyle('driver')}>
              <div
                className="flex items-center"
                style={{ gap: 5, fontWeight: 700, fontSize: 16, color: optionTitleColor('driver'), marginBottom: 5 }}>
                <FontAwesomeIcon icon={faUser} style={{ fontSize: 16 }} />
                Reject Driver
              </div>
              <p style={{ fontWeight: 300, fontSize: 14, color: optionSubColor('driver') }}>
                Approve the grouping and re-assign the driver in the Unassigned tab
              </p>
            </button>
            <button
              data-testid="gmpro-reject-grouping-option"
              onClick={() => chooseType('grouping')}
              className="focus:outline-none"
              style={optionStyle('grouping')}>
              <div
                className="flex items-center"
                style={{ gap: 5, fontWeight: 700, fontSize: 16, color: optionTitleColor('grouping'), marginBottom: 5 }}>
                <FontAwesomeIcon icon={faCarSide} style={{ fontSize: 16 }} />
                Reject Grouping
              </div>
              <p style={{ fontWeight: 300, fontSize: 14, color: optionSubColor('grouping') }}>
                Ungroup and send the passengers to the Bookings tab.
              </p>
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            <StepDots step={1} />
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 style={{ fontWeight: 500, fontSize: 20, color: ETA.purple, paddingRight: 30 }}>
            Select reason for rejecting the {rejectType}
          </h2>
          <p style={{ fontWeight: 300, fontSize: 16, color: ETA.darkGrey, margin: '4px 0 12px' }}>
            Has something gone wrong with this trip generation? If so, please select what
            went wrong using the dropdown.
          </p>
          <div className="flex items-start" style={{ gap: 12 }}>
            <ReasonDropdown
              value={reason}
              options={reasonsFor(rejectType)}
              onChange={setReason}
            />
            {noteRequired ? (
              <div className="relative" style={{ flex: 1, minWidth: 0 }}>
                <textarea
                  data-testid="gmpro-reject-note"
                  value={note}
                  maxLength={NOTE_MAX_LENGTH}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Enter reason..."
                  className="focus:outline-none"
                  style={{
                    width: '100%',
                    height: 90,
                    padding: '10px 16px 20px',
                    border: `1px solid ${ETA.inputBorder}`,
                    borderRadius: 5,
                    fontSize: 12,
                    color: ETA.darkGrey,
                    resize: 'none',
                  }}
                />
                <span
                  className="absolute"
                  style={{ bottom: 12, right: 14, fontSize: 8, color: ETA.placeholder }}>
                  {note.length}/{NOTE_MAX_LENGTH}
                </span>
              </div>
            ) : (
              <div style={{ flex: 1, minWidth: 0 }} />
            )}
          </div>
          <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
            <StepDots step={2} />
            <div className="flex" style={{ gap: 12 }}>
              <button
                data-testid="gmpro-reject-back"
                onClick={() => setStep(1)}
                className="focus:outline-none"
                style={pill(ETA.grey)}>
                Back
              </button>
              <button
                data-testid="gmpro-reject-submit"
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="focus:outline-none"
                style={pill(submitDisabled ? ETA.disabled : ETA.purple)}>
                Submit
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

StepDots.propTypes = {
    step: oneOfType([string, number])
};

ReasonDropdown.propTypes = {
    value: string,
    options: arrayOf(shape({})),
    onChange: func
};

GmproRejectSheet.propTypes = {
    grouping: shape({}),
    onClose: func,
    onRejectDriver: func,
    onRejectGrouping: func
};

export default GmproRejectSheet;
